/**
 * Frontend timezone utility functions
 * 
 * Handles conversion between UTC (stored in DynamoDB) and local time (displayed to user)
 */

/**
 * Parse year_month_day_time format and convert to local time for display
 * @param {string} yearMonthDayTime - Format: "2025-09-01_43911" or "2025-09-01-14:30:00123"
 * @returns {Date} Local date object
 */
export function parseYearMonthDayTime(yearMonthDayTime) {
  if (!yearMonthDayTime) return null;
  
  // Check if it contains time information (new format)
  if (yearMonthDayTime.includes('-', 10)) {
    // Format: "2025-09-01-14:30:00123"
    const parts = yearMonthDayTime.split('-');
    if (parts.length >= 4) {
      const datePart = `${parts[0]}-${parts[1]}-${parts[2]}`;
      const timePart = parts[3];
      
      // Extract time and milliseconds
      const timeMatch = timePart.match(/(\d{2}):(\d{2}):(\d{2})(\d{3})?/);
      if (timeMatch) {
        const [, hours, minutes, seconds, ms = '000'] = timeMatch;
        const timeString = `${hours}:${minutes}:${seconds}.${ms}`;
        
        // Create UTC date and convert to local
        const utcDate = new Date(`${datePart}T${timeString}Z`);
        return new Date(utcDate.getTime());
      }
    }
  }
  
  // Legacy format: "2025-09-01_43911" - assume 8am Central Time (UTC-6)
  const datePart = yearMonthDayTime.substring(0, 10);
  
  // Create date assuming it was entered at 8am Central Time
  // Central Time is UTC-6, so 8am Central = 2pm UTC
  const utcDate = new Date(`${datePart}T14:00:00Z`);
  
  // Return as local time
  return new Date(utcDate.getTime());
}

/**
 * Format date for display in user's local timezone
 * @param {Date|string} date - Date object or year_month_day_time string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export function formatLocalDate(date, options = {}) {
  let localDate;
  
  if (typeof date === 'string') {
    localDate = parseYearMonthDayTime(date);
  } else {
    localDate = date;
  }
  
  if (!localDate) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    ...options
  };
  
  return localDate.toLocaleDateString(undefined, defaultOptions);
}

/**
 * Format date and time for display in user's local timezone
 * @param {Date|string} date - Date object or year_month_day_time string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date and time string
 */
export function formatLocalDateTime(date, options = {}) {
  let localDate;
  
  if (typeof date === 'string') {
    localDate = parseYearMonthDayTime(date);
  } else {
    localDate = date;
  }
  
  if (!localDate) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options
  };
  
  return localDate.toLocaleString(undefined, defaultOptions);
}

/**
 * Convert local date to UTC for sending to backend
 * @param {string} dateString - Date in YYYY-MM-DD format (local)
 * @param {string} timeString - Optional time in HH:MM format (local, defaults to 08:00)
 * @returns {Object} Object with utcDate and dateStamp for backend
 */
export function convertLocalToUTC(dateString, timeString = '08:00') {
  if (!dateString) return null;
  
  // Create local date/time
  const localDateTime = new Date(`${dateString}T${timeString}:00`);
  
  // Convert to UTC
  const utcDateTime = new Date(localDateTime.getTime() + (localDateTime.getTimezoneOffset() * 60 * 1000));
  
  return {
    utcDate: utcDateTime,
    dateStamp: utcDateTime.toISOString().split('T')[0]
  };
}

/**
 * Convert date_stamp from UTC to local for display
 * Uses noon (12:00:00) as reference time to prevent date shifts across timezones
 * @param {string} utcDateStamp - UTC date in YYYY-MM-DD format
 * @returns {string} Local date in YYYY-MM-DD format
 */
export function convertDateStampFromUTC(utcDateStamp) {
  if (!utcDateStamp) return '';
  
  // Parse as noon UTC (12:00:00) to prevent date boundary shifts
  const utcDate = new Date(`${utcDateStamp}T12:00:00Z`);
  
  // Get local date components directly from the UTC date
  // This ensures we get the date as it appears in the local timezone
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Convert local date input to UTC date_stamp for backend
 * Uses noon (12:00:00) as reference time to prevent date shifts across timezones
 * @param {string} localDateString - Local date in YYYY-MM-DD format
 * @returns {string} UTC date in YYYY-MM-DD format
 */
export function convertDateStampToUTC(localDateString) {
  if (!localDateString) return '';
  
  // Create date at noon local time (12:00:00) to prevent date boundary shifts
  const localDate = new Date(`${localDateString}T12:00:00`);
  
  // Get UTC date by converting to ISO string and extracting date part
  // This preserves the date value across timezone conversions
  return localDate.toISOString().split('T')[0];
}

/**
 * Get browser's timezone offset in minutes
 * Returns positive values for west of UTC, negative for east of UTC
 * @returns {number} Offset in minutes (e.g., 360 for UTC-6, -540 for UTC+9)
 */
export function getBrowserTimezoneOffset() {
  return new Date().getTimezoneOffset();
}

/**
 * Get current local date in YYYY-MM-DD format
 * Useful for date picker default values
 * @returns {string} Current date in YYYY-MM-DD format
 */
export function getLocalDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get user's current timezone information
 * @returns {Object} Timezone information
 */
export function getUserTimezone() {
  const date = new Date();
  return {
    offset: -date.getTimezoneOffset() / 60, // Hours from UTC
    name: Intl.DateTimeFormat().resolvedOptions().timeZone,
    offsetString: date.toTimeString().match(/GMT([+-]\d{4})/)?.[1] || ''
  };
}